import { useField, useFormikContext } from 'formik';
import { ChangeEvent, useMemo } from 'react';

import { Input, InputProps } from 'theSource/components/Input';
import { FileFieldWrapperStyled } from './FileFieldStyled';
import { FormField } from '../FormField';

export interface FileFieldProps
  extends Omit<InputProps, 'error' | 'onChange' | 'value'> {
  helperText?: string;
  name: string;
  required?: boolean;
  testId?: string;
  externalError?: boolean;
  maxSize: number;
  onUpload: (name: string, url: string) => void;
  accept?: string;
  label: string;
}

export const FileField = ({
  disabled,
  fullWidth = true,
  helperText,
  id,
  name,
  required,
  testId = 'file-field',
  label,
  externalError,
  maxSize,
  onUpload,
  accept,
  ...props
}: FileFieldProps) => {
  const [{ onChange, value, ...field }, { error, touched }, { setTouched }] =
    useField({
      name,
      type: 'file',
    });

  const { isSubmitting } = useFormikContext();

  const invalid = useMemo(() => touched && Boolean(error), [error, touched]);

  const handleChange = (event: ChangeEvent) => {
    const target = event.target as HTMLInputElement;
    const myEvent = {
      target: {
        name: id,
        value: {
          fakepath: '',
          src: '',
        },
      },
    };

    if (!touched) {
      setTouched(true);
    }

    if (!target.value || target.files?.length === 0) {
      onUpload(id, myEvent.target.value.src);
      onChange(myEvent);
      return;
    }

    myEvent.target.value.fakepath = target.value;
    const file: File = (target.files as FileList)[0];
    if (file.size > maxSize) {
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      myEvent.target.value.src = reader.result as string;
      onUpload(id, myEvent.target.value.src);
    };

    onChange(myEvent);
  };

  return (
    <FileFieldWrapperStyled>
      <FormField
        disabled={disabled || isSubmitting}
        error={invalid || externalError}
        fullWidth={fullWidth}
        helperText={invalid ? error : helperText}
        inputId={id}
        label={label}
        required={required}
        testId={testId}
      >
        <Input
          data-testid="file-field-input"
          onChange={handleChange}
          id={id}
          type="file"
          value={value.fakepath}
          inputProps={{ accept }}
          {...field}
          {...props}
        />
      </FormField>
    </FileFieldWrapperStyled>
  );
};
